import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { writtenTestApi } from '../services/api';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';

interface WrittenTestPageProps {
  applicant: any;
}

interface Test {
  id: number;
  title: string;
  description: string;
  type: 'LOGIC' | 'CASE_STUDY';
  time_limit: number | null;
}

interface ApplicantTest {
  id: number;
  test_id: number;
  status: 'NOT_STARTED' | 'IN_PROGRESS' | 'SUBMITTED' | 'EVALUATED';
  started_at: string | null;
  submitted_at: string | null;
  score: number | null;
  test: Test;
  locked?: boolean;
}

interface Question {
  id: number;
  question_type: 'MULTIPLE_CHOICE' | 'SHORT_ANSWER' | 'ESSAY';
  question: string;
  options: string[] | null;
  score: number;
  order_num: number;
}

interface TestSession {
  id: number;
  test: Test & { questions: Question[] };
  started_at: string;
  answers: { [questionId: number]: string };
}

const WrittenTestPage: React.FC<WrittenTestPageProps> = ({ applicant }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const confirmDialog = useConfirm();
  const [tests, setTests] = useState<ApplicantTest[]>([]);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<TestSession | null>(null);
  const [remainingTime, setRemainingTime] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showConfirm, setShowConfirm] = useState<ApplicantTest | null>(null);
  const [tabSwitchCount, setTabSwitchCount] = useState(0);
  const [showWarning, setShowWarning] = useState(false);
  const [saveFailCount, setSaveFailCount] = useState(0);

  useEffect(() => {
    loadTests();
  }, [applicant]);

  // 부정행위 방지: 시험 중일 때만 활성화
  useEffect(() => {
    if (!session) return;

    // 1. 페이지 이탈 경고
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '시험이 진행 중입니다. 페이지를 떠나시겠습니까?';
      return e.returnValue;
    };

    // 2. 뒤로가기 방지
    const handlePopState = (e: PopStateEvent) => {
      window.history.pushState(null, '', window.location.href);
      toast.warning('시험 중에는 뒤로가기를 사용할 수 없습니다.');
    };

    // 3. 탭/창 전환 감지
    const handleVisibilityChange = () => {
      if (document.hidden) {
        setTabSwitchCount(prev => {
          const newCount = prev + 1;
          if (newCount >= 3) {
            toast.warning('경고: 화면 이탈이 3회 감지되었습니다. 추가 이탈 시 자동 제출될 수 있습니다.');
          } else {
            setShowWarning(true);
            setTimeout(() => setShowWarning(false), 3000);
          }
          return newCount;
        });
      }
    };

    // 4. 우클릭 방지
    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
      toast.warning('시험 중에는 우클릭을 사용할 수 없습니다.');
    };

    // 5. 복사/붙여넣기 방지 (단, 서술형 입력은 허용)
    const handleCopy = (e: ClipboardEvent) => {
      e.preventDefault();
      toast.warning('시험 중에는 복사를 할 수 없습니다.');
    };

    const handlePaste = (e: ClipboardEvent) => {
      const target = e.target as HTMLElement;
      // textarea(서술형)에서만 붙여넣기 허용하지 않음
      e.preventDefault();
      toast.warning('시험 중에는 붙여넣기를 할 수 없습니다.');
    };

    // 6. 드래그 앤 드롭 방지 (외부 텍스트 드롭 차단)
    const handleDrop = (e: DragEvent) => {
      e.preventDefault();
    };
    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
    };

    // 7. 키보드 단축키 방지 (Ctrl+C, Ctrl+V, Ctrl+P, 새로고침 등)
    const handleKeyDown = (e: KeyboardEvent) => {
      // F5 새로고침 방지
      if (e.key === 'F5') {
        e.preventDefault();
        toast.warning('시험 중에는 새로고침을 할 수 없습니다.');
        return;
      }
      // Ctrl+R / Cmd+R 새로고침 방지
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'r') {
        e.preventDefault();
        toast.warning('시험 중에는 새로고침을 할 수 없습니다.');
        return;
      }
      if (e.ctrlKey || e.metaKey) {
        if (['c', 'v', 'p', 'a', 's'].includes(e.key.toLowerCase())) {
          e.preventDefault();
          if (e.key.toLowerCase() === 'c') {
            toast.warning('시험 중에는 복사를 할 수 없습니다.');
          } else if (e.key.toLowerCase() === 'v') {
            toast.warning('시험 중에는 붙여넣기를 할 수 없습니다.');
          }
        }
      }
      // F12 개발자도구 방지
      if (e.key === 'F12') {
        e.preventDefault();
      }
    };

    // 히스토리 상태 추가 (뒤로가기 방지용)
    window.history.pushState(null, '', window.location.href);

    // 이벤트 리스너 등록
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('copy', handleCopy);
    document.addEventListener('paste', handlePaste);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('drop', handleDrop);
    document.addEventListener('dragover', handleDragOver);

    // 클린업
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('paste', handlePaste);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('drop', handleDrop);
      document.removeEventListener('dragover', handleDragOver);
    };
  }, [session]);

  const loadTests = async () => {
    try {
      const data = await writtenTestApi.getMyTests();
      setTests(data);

      // 새로고침 복원: IN_PROGRESS 시험이 있으면 자동으로 시험 화면 복원
      const inProgress = data.find((t: ApplicantTest) => t.status === 'IN_PROGRESS');
      if (inProgress && !session) {
        try {
          const result = await writtenTestApi.startTest(inProgress.test_id);
          setSession({
            id: result.id,
            test: result.test,
            started_at: result.started_at,
            answers: result.saved_answers || {},
          });
          if (result.resumed) {
            toast.warning('페이지가 새로고침되었습니다. 시험을 이어서 진행해주세요.');
          }
        } catch {
          // 시간 초과 등으로 자동 제출된 경우 — 목록만 갱신
          const refreshed = await writtenTestApi.getMyTests();
          setTests(refreshed);
        }
      }
    } catch (err) {
      // 시험 목록 로드 실패
    } finally {
      setLoading(false);
    }
  };

  // 타이머
  useEffect(() => {
    if (!session || !session.test.time_limit) return;

    const startTime = new Date(session.started_at).getTime();
    const endTime = startTime + session.test.time_limit * 60 * 1000;

    const timer = setInterval(() => {
      const now = Date.now();
      const remaining = Math.max(0, Math.floor((endTime - now) / 1000));
      setRemainingTime(remaining);

      if (remaining === 0) {
        clearInterval(timer);
        handleSubmit();
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [session]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleStartTest = async (applicantTest: ApplicantTest) => {
    try {
      const result = await writtenTestApi.startTest(applicantTest.test_id);
      setSession({
        id: result.id,
        test: result.test,
        started_at: result.started_at,
        answers: result.saved_answers || {},
      });
      setShowConfirm(null);
    } catch (err: any) {
      toast.error(err.response?.data?.message || '시험 시작에 실패했습니다.');
    }
  };

  const handleAnswerChange = async (questionId: number, answer: string) => {
    if (!session) return;

    setSession({
      ...session,
      answers: { ...session.answers, [questionId]: answer },
    });

    // 자동 저장
    try {
      await writtenTestApi.saveAnswer(session.id, questionId, answer);
      setSaveFailCount(0);
    } catch {
      setSaveFailCount(prev => {
        const count = prev + 1;
        if (count >= 3) {
          toast.error('답변 저장에 실패하고 있습니다. 네트워크 연결을 확인해주세요.');
        }
        return count;
      });
    }
  };

  const handleSubmit = async () => {
    if (!session) return;
    if (submitting) return;

    const unanswered = session.test.questions.filter(
      q => !session.answers[q.id] || !session.answers[q.id].trim()
    );

    if (unanswered.length > 0 && remainingTime !== 0) {
      const ok = await confirmDialog({
        title: '미답변 문제가 있습니다',
        message: `${unanswered.length}개의 문제가 답변되지 않았습니다. 제출하시겠습니까?`,
        confirmText: '제출',
        variant: 'danger',
      });
      if (!ok) return;
    }

    setSubmitting(true);
    try {
      await writtenTestApi.submitTest(session.id);
      toast.success('시험이 제출되었습니다.');
      setSession(null);
      loadTests();
    } catch (err: any) {
      toast.error(err.response?.data?.message || '제출에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  // 시험 진행 화면
  if (session) {
    return (
      <div className="min-h-screen bg-slate-100 select-none">
        {/* 화면 이탈 경고 */}
        {showWarning && (
          <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[100] animate-pulse">
            <div className="bg-red-600 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span className="font-medium">화면 이탈이 감지되었습니다! ({tabSwitchCount}/3)</span>
            </div>
          </div>
        )}

        {/* 헤더 - 타이머 */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h1 className="text-base font-semibold text-slate-800">{session.test.title}</h1>
              {tabSwitchCount > 0 && (
                <span className="text-xs px-2 py-1 bg-red-100 text-red-600 rounded-full">
                  이탈 {tabSwitchCount}회
                </span>
              )}
            </div>
            <div className="flex items-center gap-4">
              {session.test.time_limit && remainingTime !== null && (
                <div className={`text-lg font-mono font-bold ${remainingTime < 300 ? 'text-red-600' : 'text-slate-700'}`}>
                  {formatTime(remainingTime)}
                </div>
              )}
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg disabled:opacity-50"
              >
                {submitting ? '제출 중...' : '제출하기'}
              </button>
            </div>
          </div>
        </header>

        {/* 부정행위 방지 안내 배너 */}
        <div className="max-w-4xl mx-auto px-4 pt-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 flex items-center gap-2 text-xs text-amber-700">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span>복사, 붙여넣기, 우클릭, 화면 이탈이 제한됩니다. 새로고침 시 작성 내용이 초기화될 수 있습니다.</span>
          </div>
        </div>

        <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
          {session.test.questions.map((q, idx) => (
            <div key={q.id} className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-start gap-3 mb-4">
                <span className="flex-shrink-0 w-8 h-8 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center text-sm font-semibold">
                  {idx + 1}
                </span>
                <div className="flex-1">
                  {q.question.split(/(\[Image:\s*[^\]]+\])/).map((part, i) => {
                    const imgMatch = part.match(/\[Image:\s*([^\]]+)\]/);
                    if (imgMatch) {
                      const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:4001/api').replace(/\/api\/?$/, '');
                      const rawSrc = imgMatch[1].trim();
                      const src = rawSrc.startsWith('/api/') ? `${baseUrl}${rawSrc}` : rawSrc;
                      return <img key={i} src={src} alt="문제 이미지" className="my-3 max-w-full rounded-lg border border-slate-200" />;
                    }
                    return <p key={i} className="text-slate-800 whitespace-pre-wrap">{part}</p>;
                  })}
                  {/* 배점은 관리자만 확인 */}
                </div>
              </div>

              {q.question_type === 'MULTIPLE_CHOICE' && q.options && (
                <div className="space-y-2 ml-11">
                  {q.options.map((option, optIdx) => (
                    <label
                      key={optIdx}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        session.answers[q.id] === option
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name={`question-${q.id}`}
                        value={option}
                        checked={session.answers[q.id] === option}
                        onChange={() => handleAnswerChange(q.id, option)}
                        className="w-4 h-4 text-indigo-600"
                      />
                      <span className="text-sm text-slate-700">{option}</span>
                    </label>
                  ))}
                </div>
              )}

              {q.question_type === 'SHORT_ANSWER' && (
                <div className="ml-11">
                  <input
                    type="text"
                    value={session.answers[q.id] || ''}
                    onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                    placeholder="답변을 입력하세요"
                    className="w-full max-w-md px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              )}

              {q.question_type === 'ESSAY' && (
                <div className="ml-11">
                  <textarea
                    value={session.answers[q.id] || ''}
                    onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                    placeholder="답변을 작성해주세요..."
                    rows={15}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-y"
                  />
                  <p className="text-xs text-slate-400 mt-1 text-right">
                    {(session.answers[q.id] || '').length}자
                  </p>
                </div>
              )}
            </div>
          ))}

          <div className="flex justify-center pb-8">
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="px-8 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg disabled:opacity-50"
            >
              {submitting ? '제출 중...' : '시험 제출하기'}
            </button>
          </div>
        </main>
      </div>
    );
  }

  // 시험 목록 화면
  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate('/')}
            className="text-slate-400 hover:text-slate-600"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-base font-semibold text-slate-800">서면 평가</h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        {loading ? (
          <div className="text-center py-12 text-slate-400">로딩 중...</div>
        ) : tests.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-slate-500">진행 가능한 시험이 없습니다.</p>
          </div>
        ) : (
          tests.map((t) => (
            <div key={t.id} className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-slate-800">{t.test.title}</h3>
                  <p className="text-sm text-slate-500 mt-1">{t.test.description}</p>
                  <div className="flex items-center gap-4 mt-3 text-xs text-slate-400">
                    <span className={`px-2 py-1 rounded ${
                      t.test.type === 'LOGIC' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                    }`}>
                      {t.test.type === 'LOGIC' ? '논리력 평가' : '케이스 스터디'}
                    </span>
                    {t.test.time_limit && (
                      <span>제한시간: {t.test.time_limit}분</span>
                    )}
                    {!t.test.time_limit && (
                      <span>시간 제한 없음</span>
                    )}
                  </div>
                </div>
                <div className="flex-shrink-0">
                  {t.locked ? (
                    <span className="px-4 py-2 bg-slate-100 text-slate-400 text-sm font-medium rounded-lg flex items-center gap-1.5">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                      잠김
                    </span>
                  ) : t.status === 'NOT_STARTED' ? (
                    <button
                      onClick={() => setShowConfirm(t)}
                      className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg"
                    >
                      시작하기
                    </button>
                  ) : t.status === 'IN_PROGRESS' ? (
                    <span className="px-4 py-2 bg-orange-100 text-orange-700 text-sm font-medium rounded-lg">
                      진행중
                    </span>
                  ) : t.status === 'SUBMITTED' ? (
                    <span className="px-4 py-2 bg-green-100 text-green-700 text-sm font-medium rounded-lg">
                      제출완료
                    </span>
                  ) : t.status === 'EVALUATED' ? (
                    <span className="px-4 py-2 bg-slate-100 text-slate-700 text-sm font-medium rounded-lg">
                      평가완료
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
          ))
        )}
      </main>

      {/* 시작 확인 모달 */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-2">시험을 시작하시겠습니까?</h3>
            <p className="text-sm text-slate-500 mb-4">
              {showConfirm.test.title}
            </p>
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4 space-y-2">
              {showConfirm.test.time_limit && (
                <p className="text-sm text-amber-700">
                  <strong>제한시간:</strong> {showConfirm.test.time_limit}분 (시간 초과 시 자동 제출)
                </p>
              )}
              <p className="text-sm text-amber-700">
                <strong>부정행위 방지 안내:</strong>
              </p>
              <ul className="text-xs text-amber-600 list-disc list-inside space-y-1">
                <li>시험 중 다른 탭/창으로 이동 시 경고가 표시됩니다</li>
                <li>복사/붙여넣기가 제한됩니다</li>
                <li>우클릭 및 뒤로가기가 제한됩니다</li>
                <li>화면 이탈 3회 이상 시 감점될 수 있습니다</li>
              </ul>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirm(null)}
                className="flex-1 py-2.5 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
              >
                취소
              </button>
              <button
                onClick={() => handleStartTest(showConfirm)}
                className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg"
              >
                시작하기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WrittenTestPage;
